import { CloseOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton, Stack } from '@mui/material';
import { createColumnHelper, RowSelectionState } from '@tanstack/react-table';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import DistributorGroupAutocompleteForUser, {
  EnhancedDistributorGroup,
} from '@/components/features/UserDetails/components/DistributorGroupAutocompleteForUser';
import NotSpecifiedRoleDialog from '@/components/shared/Dialog/NotSpecifiedRoleDialog';
import { DistributorRoleSwapModal } from '@/components/shared/DistributorRoleSwapModal';
import RoleSelect from '@/components/shared/RoleSelect';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import IndeterminateCheckbox from '@/components/shared/Table/IndeterminateCheckbox';
import TableSelectionActions from '@/components/shared/Table/TableSelectionActions';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useDistributorRoleManagement } from '@/hooks/swapRoles/useDistributorRoleManagement';
import { useDistributorGroupByUser } from '@/hooks/useDistributorGroupByUser';
import { useManageDistributorGroupUsers } from '@/hooks/useManageDistributorGroupUsers';
import { useManageUserDistributorGroups } from '@/hooks/useManageUserDistributorGroups';
import { usePendingRoleSelection } from '@/hooks/usePendingRoleSelection';
import { useSnackbar } from '@/hooks/useSnackbar';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import type { components } from '@/shared/api/openapi';
import { normalizeRole } from '@/shared/constants/userGroupRole';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

type DistributorGroupItem = NonNullable<
  components['schemas']['GetDistributorGroupByUserEndpoint_GetDistributorGroupByUserEndpointPagedResponse']['items']
>[number];

interface DistributorGroupsTabProps {
  userId: string | undefined;
  email: string | undefined;
}

const columnHelper = createColumnHelper<DistributorGroupItem>();

function DistributorGroupsTab({ userId, email }: DistributorGroupsTabProps) {
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { organizationId } = useOrganizationId();
  const snackbar = useSnackbar();
  const addButtonRef = useRef<HTMLButtonElement | null>(null);

  const [selectedGroupNames, setSelectedGroupNames] = useState<EnhancedDistributorGroup[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { markPending, resolvePending, isBlocked, proceed, reset } = usePendingRoleSelection<number>();

  const { handleRoleChange, swapModalOpen, closeSwapModal, swapModalData, confirmSwap, isRoleChanging, isSwappingRole } =
    useDistributorRoleManagement();

  const { pagedDistributorGroups, isLoading, isRefetching, error } = useDistributorGroupByUser(
    userId || '',
    apiPage,
    pagination.pageSize,
    'DistributorName',
  );

  const distributorGroups = useMemo(() => {
    return pagedDistributorGroups?.items || [];
  }, [pagedDistributorGroups?.items]);

  const totalCount = pagedDistributorGroups?.totalCount ?? 0;

  const ensureValidPageAfterRemove = useCallback(
    (newTotalCount: number) => {
      const maxPageIndex = Math.max(0, Math.ceil(newTotalCount / pagination.pageSize) - 1);

      if (pagination.pageIndex > maxPageIndex) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: maxPageIndex,
        }));
      }
    },
    [pagination.pageIndex, pagination.pageSize, setPagination],
  );

  const {
    removeUser,
    removeDistributors,
    isMutating: isManagingGroups,
    removeUserMutation,
    removeDistributorsBatchMutation,
  } = useManageDistributorGroupUsers({ snackbar });

  const { addGroups, loading: isAddingGroups } = useManageUserDistributorGroups({ snackbar });

  const handleAddGroups = async () => {
    if (!email || !selectedGroupNames || selectedGroupNames.length === 0 || isAddingGroups || isManagingGroups) {
      return;
    }

    try {
      const groupNames = selectedGroupNames.map((g) => g.distributorName).filter((name): name is string => typeof name === 'string');

      const result = await addGroups(email, groupNames);

      if (result) {
        setSelectedGroupNames([]);
        setRowSelection({});
        markPending(selectedGroupNames.map((g) => g.systemId).filter((systemId): systemId is number => systemId !== undefined));
        snackbar.showSuccess('Distributor groups added successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar, 'Failed to add distributor groups');
    }
  };

  const handleRemoveGroup = useCallback(
    async (response: DistributorGroupItem) => {
      if (!email || !response.group?.systemId || response?.role == null || isManagingGroups) {
        return;
      }

      try {
        const result = await removeUser(response.group.systemId, email, response.role);

        if (result) {
          setRowSelection({});
          const newTotal = (totalCount ?? 0) - 1;
          ensureValidPageAfterRemove(newTotal);
          resolvePending([response.group.systemId]);
          snackbar.showSuccess('Distributor group removed successfully');
        }
      } catch (err) {
        handleSnackbarError(err, snackbar, 'Failed to remove distributor group');
      }
    },
    [email, removeUser, isManagingGroups, snackbar, ensureValidPageAfterRemove, totalCount, resolvePending],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </Box>
        ),
        size: 5,
        minSize: 5,
        maxSize: 5,
      }),
      columnHelper.accessor('group', {
        header: 'Name',
        cell: (info) => info.getValue()?.distributorName || '-',
      }),
      columnHelper.accessor('role', {
        header: 'Role',
        cell: (info) => {
          const distributorGroup = info.row.original;
          const role = normalizeRole(info.getValue());
          const absoluteIndex = pagination.pageIndex * pagination.pageSize + info.row.index;

          return (
            <RoleSelect
              value={role}
              onChange={(newRole) => {
                if (email && distributorGroup.group?.systemId) {
                  handleRoleChange(email, distributorGroup.group.systemId, distributorGroup.group.distributorName || '', newRole);
                  resolvePending([distributorGroup.group.systemId]);
                }
              }}
              testId={`role-select-${absoluteIndex}`}
              disabled={
                isAddingGroups ||
                isManagingGroups ||
                isRoleChanging ||
                isSwappingRole ||
                isRefetching ||
                removeUserMutation.isPending ||
                removeDistributorsBatchMutation.isPending
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <Box sx={{ textAlign: 'right', width: '100%' }}></Box>,
        cell: (info) => {
          const distributorGroup = info.row.original;
          const absoluteIndex = pagination.pageIndex * pagination.pageSize + info.row.index;

          const isRemovingDistributor =
            removeUserMutation.variables?.distributorGroupId === distributorGroup.group?.systemId &&
            (isRefetching || removeUserMutation.isPending);

          const isRemovingDistributorInBatch =
            removeDistributorsBatchMutation.variables?.groups.some(
              (distributorFromBatch) => distributorFromBatch.distributorGroupId === distributorGroup.group?.systemId,
            ) &&
            (isRefetching || removeDistributorsBatchMutation.isPending);

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveGroup(distributorGroup);
                }}
                disabled={
                  isAddingGroups ||
                  isRoleChanging ||
                  isSwappingRole ||
                  isRemovingDistributor ||
                  removeUserMutation.isPending ||
                  removeDistributorsBatchMutation.isPending ||
                  isRefetching ||
                  isRemovingDistributorInBatch
                }
                data-testid={`remove-distributor-groups-${absoluteIndex}`}
              >
                {isRemovingDistributor || isRemovingDistributorInBatch ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <CloseOutlined sx={{ fontSize: '14px' }} />
                )}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [
      pagination.pageIndex,
      pagination.pageSize,
      isAddingGroups,
      isManagingGroups,
      isSwappingRole,
      isRoleChanging,
      handleRoleChange,
      handleRemoveGroup,
      isRefetching,
      removeUserMutation,
      removeDistributorsBatchMutation,
      email,
      resolvePending,
    ],
  );

  const handleRemoveSelectedDistributors = async () => {
    if (!email || isManagingGroups) return;

    const groupsToRemove = distributorGroups
      .filter((u) => u.group?.systemId && rowSelection[u.group.systemId.toString()] && u.role != null)
      .map((u) => ({
        distributorGroupId: u.group!.systemId!,
        role: u.role!,
      }));

    if (groupsToRemove.length === 0) return;

    try {
      const success = await removeDistributors(email, groupsToRemove);

      if (success) {
        const removedCount = groupsToRemove.length;
        const newTotal = (totalCount ?? removedCount) - removedCount;
        setRowSelection({});
        ensureValidPageAfterRemove(newTotal);
        resolvePending(groupsToRemove.map((g) => g.distributorGroupId));
        snackbar.showSuccess('Selected groups removed successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };
  const handleChangePage = (_event: unknown, newPage: number) => {
    setRowSelection({});
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowSelection({});
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const tableWithData = useBaseTable<DistributorGroupItem>({
    tableData: pagedDistributorGroups?.items ?? [],
    columns,
    isRowSelectionEnabled: true,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: totalCount ?? 0,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
    getRowId: (row, index) => row.group?.systemId?.toString() ?? `row-${index}`,
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-distributors'
      >
        Error loading distributors: {error.message}
      </Alert>
    );
  }

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack
        direction='row'
        spacing={1}
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap-reverse',
        }}
      >
        <TableSelectionActions
          selectedCount={selectedCount}
          totalCount={distributorGroups.length}
          onRemove={handleRemoveSelectedDistributors}
          selectionLabel='Selected Groups'
          removeButtonText='Remove Groups'
          testIdPrefix='selected-distributor-groups'
        />

        <DistributorGroupAutocompleteForUser
          userId={userId || ''}
          organizationId={organizationId}
          value={selectedGroupNames}
          onChange={(_: React.SyntheticEvent, newValue: EnhancedDistributorGroup[]) => setSelectedGroupNames(newValue)}
          onApply={handleAddGroups}
          buttonRef={addButtonRef}
          disabled={isAddingGroups || isManagingGroups}
        />
      </Stack>

      <BaseTable<DistributorGroupItem>
        {...tableWithData}
        testId='distributor-groups-table'
        ariaLabel='Distributor Groups list'
        noDataMessage='No distributor groups assigned.'
        loadingData={{
          isLoading: isLoading,
          loadingMessage: 'Loading...',
        }}
      />

      <DistributorRoleSwapModal
        open={swapModalOpen}
        onClose={closeSwapModal}
        onConfirm={confirmSwap}
        swapModalData={swapModalData}
        loading={isSwappingRole || isRoleChanging}
      />
      <NotSpecifiedRoleDialog open={isBlocked} onConfirm={proceed} onCancel={reset} />
    </Box>
  );
}

export default DistributorGroupsTab;
